import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../assets/styles/welcome-splash.scss';

type Props = {
    durationMs?: number;       // fallback, gdyby video nie wywołało 'ended'
    nextPath?: string;
    oncePerSession?: boolean;
    videoSrc?: string;         // np. '/welcome.mp4'
    endHoldMs?: number;        // ile trzymać biały ekran z "Witaj" po końcu filmu (pełna widoczność)
    startFadeMs?: number;      // czas rozjaśnienia na starcie
    endFadeMs?: number;        // czas rozjaśnienia na końcu (do białego)
    helloFadeMs?: number;      // czas wygaszania napisu "Witaj"
};

export default function WelcomeSplash({
    durationMs = 3500,
    nextPath = '/admin',
    oncePerSession = true,
    videoSrc = '/welcome.mp4',
    endHoldMs = 1000,          // 1s pełnej widoczności
    startFadeMs = 400,
    endFadeMs = 450,
    helloFadeMs = 550,         // ~0.55s wygaszania napisu
}: Props) {
    const nav = useNavigate();
    const videoRef = useRef<HTMLVideoElement | null>(null);

    const timeoutsRef = useRef<number[]>([]);
    const didEndRef = useRef(false);   // strażnik przed wielokrotnym kończeniem
    const didSkipRef = useRef(false);  // strażnik, gdy użytkownik pominie

    const [started, setStarted] = useState(false); // chowamy biały overlay na starcie
    const [ending, setEnding] = useState(false);   // pokazujemy biały overlay na końcu
    const [showHello, setShowHello] = useState(false);

    useEffect(() => {
        // opcjonalnie: raz na sesję
        // if (oncePerSession && sessionStorage.getItem('welcomed') === '1') {
        //   nav(nextPath, { replace: true }); return;
        // }

        const v = videoRef.current;
        if (!v) return;

        // fallback timeout — gdyby film się nie skończył
        timeoutsRef.current.push(window.setTimeout(() => beginEnding(), durationMs));

        const onPlay = () => setStarted(true);
        const onEnded = () => beginEnding();

        v.addEventListener('play', onPlay);
        v.addEventListener('ended', onEnded);

        // iOS: autoplay wymaga muted + playsInline
        v.play().catch(() => {
            const onCanPlay = () => {
                v.play().catch(() => { });
                v.removeEventListener('canplay', onCanPlay);
            };
            v.addEventListener('canplay', onCanPlay);
        });

        return () => {
            v.removeEventListener('play', onPlay);
            v.removeEventListener('ended', onEnded);
            // cleanup timeoutów
            timeoutsRef.current.forEach(id => window.clearTimeout(id));
            timeoutsRef.current = [];
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const beginEnding = () => {
        if (didEndRef.current) return;   // już weszliśmy — nie dubluj
        didEndRef.current = true;

        // wyczyść dotychczasowe timeouty (w tym fallback)
        timeoutsRef.current.forEach(id => window.clearTimeout(id));
        timeoutsRef.current = [];

        // fade do białego
        setEnding(true);

        // po zakończeniu fade pokaż "Witaj"
        timeoutsRef.current.push(
            window.setTimeout(() => {
                if (didSkipRef.current) return;
                setShowHello(true);
            }, endFadeMs)
        );

        // po 1s hold zacznij wygaszanie "Witaj"
        timeoutsRef.current.push(
            window.setTimeout(() => {
                if (didSkipRef.current) return;
                setShowHello(false);

                // po zakończeniu wygaszania — przejście dalej
                timeoutsRef.current.push(
                    window.setTimeout(() => {
                        if (didSkipRef.current) return;
                        goNext();
                    }, helloFadeMs)
                );
            }, endFadeMs + endHoldMs)
        );
    };

    const goNext = () => {
        if (oncePerSession) sessionStorage.setItem('welcomed', '1');
        nav(nextPath, { replace: true });
    };

    // klik/klawisz — pomiń natychmiast
    const onClickSkip = () => {
        didSkipRef.current = true;
        timeoutsRef.current.forEach(id => window.clearTimeout(id));
        timeoutsRef.current = [];
        goNext();
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClickSkip();
        }
    };

    return (
        <div
            className={`splash-video ${started ? 'is-started' : ''} ${ending ? 'is-ending' : ''}`}
            role="dialog"
            aria-label="Witamy w aplikacji"
            onKeyDown={onKeyDown}
        >
            <video
                ref={videoRef}
                className="splash-video__media"
                src={videoSrc}
                autoPlay
                muted
                playsInline
                preload="auto"
                controls={false}
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                aria-hidden
            />

            {/* Fade overlay – sterowany klasami is-started / is-ending */}
            <div
                className="splash-video__fade"
                style={{
                    ['--startFadeMs' as any]: `${startFadeMs}ms`,
                    ['--endFadeMs' as any]: `${endFadeMs}ms`,
                }}
                aria-hidden
            />

            {/* Tekst "Witaj" – pojawia się po rozjaśnieniu, stoi 1s, zanika 550ms, dopiero potem nawigacja */}
            <div
                className={`splash-video__hello ${showHello ? 'is-visible' : ''}`}
                style={{ ['--helloFadeMs' as any]: `${helloFadeMs}ms` }}
                aria-hidden
            >
                Witaj w PolyLect
            </div>

            <button className="splash-video__skip" onClick={onClickSkip} aria-label="Pomiń ekran powitalny">
                Pomiń
            </button>
        </div>
    );
}
